import React, { useState } from "react";
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Paper,
  useTheme,
  useMediaQuery,
  Checkbox,
  Grid,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

/**
 * A component for rendering and editing a date-text item
 */
const DateTextItem = ({
  id,
  description,
  date,
  textValue,
  completed = false,
  onUpdate,
  onDelete,
  editable = true,
  draggable = true,
  isTemplateView = false,
}) => {
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [isEditingText, setIsEditingText] = useState(false);
  const [itemDescription, setItemDescription] = useState(description || "");
  const [itemTextValue, setItemTextValue] = useState(textValue || "");
  const [itemCompletionDate, setItemCompletionDate] = useState(date || "");
  // const [dateValue, setDateValue] = useState("")
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleToggleComplete = () => {
    onUpdate(id, { completed: !completed });
  };

  const handleDescriptionChange = (e) => {
    setItemDescription(e.target.value);
  };

  const handleDescriptionBlur = () => {
    setIsEditingDescription(false);
    if (itemDescription.trim() !== description) {
      onUpdate(id, { description: itemDescription.trim() });
    }
  };

  // const handleTextValueChange = (e) => {
  //   setItemTextValue(e.target.value);
  // };

  // const handleTextValueBlur = () => {
  //   setIsEditingText(false);
  //   if (itemTextValue !== textValue) {
  //     onUpdate(id, { textValue: itemTextValue });
  //   }
  // };

  const handleTextValueChange = (e) => {
    const newValue = e.target.value;
    setItemTextValue(!newValue ? "" : newValue);
    setItemCompletionDate(!newValue ? "" : dayjs().format("YYYY-MM-DD"));
  };

  const handleTextValueBlur = () => {
    setIsEditingText(false);
    if (itemTextValue !== textValue) {
      onUpdate(id, {
        textValue: itemTextValue,
        date: itemCompletionDate,
        completed: itemTextValue ? true : false,
      });
    }
  };

  const handleDateChange = (newDate) => {
    onUpdate(id, { date: newDate ? newDate.toISOString() : null });
  };

  const handleKeyDown = (e, field) => {
    if (e.key === "Enter") {
      if (field === "description") {
        handleDescriptionBlur();
      } else if (field === "textValue") {
        handleTextValueBlur();
      }
    }
  };

  const handleDelete = () => {
    onDelete(id);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Paper
        elevation={0}
        sx={{
          p: 1,
          mb: 1,
          borderRadius: 1,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          "&:hover": {
            bgcolor: "action.hover",
          },
        }}
      >
        <Grid container spacing={2} columns={24}>
          {draggable && (
            <Grid size={{ xs: 1, sm: 1, md: 1 }}>
              <IconButton
                size="small"
                sx={{
                  cursor: "grab",
                  color: "text.disabled",
                  mr: 0.5,
                  mt: 0.5,
                  visibility: isMobile ? "hidden" : "visible",
                }}
              >
                <DragIndicatorIcon fontSize="small" />
              </IconButton>
            </Grid>
          )}
          {/* Description Field */}
          <Grid container size={{ xs: 18, sm: 18, md: 22 }} columns={24}>
            <Grid size={{ xs: 24, sm: 24, md: 10 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                {isEditingDescription && editable && isTemplateView ? (
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Description"
                    value={itemDescription}
                    onChange={handleDescriptionChange}
                    onBlur={handleDescriptionBlur}
                    onKeyDown={(e) => handleKeyDown(e, "description")}
                    autoFocus
                    size="small"
                  />
                ) : (
                  <Box
                    variant="body1"
                    onClick={() => editable && setIsEditingDescription(true)}
                    sx={{
                      cursor: editable ? "pointer" : "default",
                      wordBreak: "break-word",
                      fontWeight: 500,
                      lineHeight: "normal",
                      minHeight: "2.5em",
                      display: "flex",
                      alignItems: "center",
                      ml: 1.5,
                    }}
                  >
                    {description || "Click to add description..."}
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid
              size={{ xs: 24, sm: 24, md: 5 }}
              justifyContent="center"
              alignItems="center"
            >
              <Box
                variant="body1"
                onClick={() => editable && setIsEditingDescription(true)}
                sx={{
                  cursor: editable ? "pointer" : "default",
                  wordBreak: "break-word",
                  fontWeight: 500,
                  lineHeight: "normal",
                  minHeight: "2.5em",
                  display: "flex",
                  alignItems: "center",
                  ml: isMobile ? 1.5 : 0,
                }}
              >
                {date || ""}
              </Box>
            </Grid>
            <Grid size={{ xs: 24, sm: 24, md: 9 }}>
              {isEditingText && editable ? (
                <TextField
                  fullWidth
                  multiline
                  variant="outlined"
                  label="Text Value"
                  value={itemTextValue}
                  onChange={handleTextValueChange}
                  onBlur={handleTextValueBlur}
                  onKeyDown={(e) => handleKeyDown(e, "textValue")}
                  size="small"
                />
              ) : (
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Text Value"
                  value={textValue || ""}
                  onClick={() => editable && setIsEditingText(true)}
                  disabled={!editable}
                  size="small"
                  placeholder="Click to add text..."
                  sx={{
                    cursor: editable ? "pointer" : "default",
                  }}
                />
              )}
            </Grid>
          </Grid>
          {editable && isTemplateView && (
            <Grid
              size={{ xs: 1, sm: 1, md: 1 }}
              sx={{ justifyContent: "right" }}
            >
              <IconButton
                size="small"
                onClick={handleDelete}
                sx={{ color: "error.light", mt: 0.5 }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Grid>
          )}
        </Grid>
      </Paper>
    </LocalizationProvider>
  );
};

export default DateTextItem;
